const API = process.env.REACT_APP_API_URL || '';

function handleUnauthorized() {
  localStorage.removeItem('token');
  window.dispatchEvent(new Event('auth:expired'));
}

function headers() {
  const h = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

function authHeader() {
  const h = {};
  const token = localStorage.getItem('token');
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

async function request(path, opts = {}) {
  const timeoutMs = opts._timeout || 120000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const hadToken = !!localStorage.getItem('token');
  try {
    const res = await fetch(`${API}${path}`, { ...opts, headers: headers(), signal: controller.signal });
    clearTimeout(timer);
    let data;
    try {
      data = await res.json();
    } catch {
      data = { error: `Ошибка сервера (${res.status})` };
    }
    if (res.status === 401 && hadToken) {
      handleUnauthorized();
      throw new Error('Сессия истекла. Войдите снова.');
    }
    if (!res.ok) throw new Error(data.error || `Ошибка сервера (${res.status})`);
    return data;
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') throw new Error('Превышено время ожидания (2 мин). Попробуйте ещё раз.');
    throw err;
  }
}

export const api = {
  login: (email, password) => request('/api/users/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  me: () => request('/api/users/me'),
  getUsers: () => request('/api/users'),
  getNextName: () => request('/api/users/next-name'),
  createUser: (data) => request('/api/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/api/users/${id}`, { method: 'DELETE' }),
  changePassword: (currentPassword, newPassword) => request('/api/users/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  chatOpenAI: (messages, model, session_id) => request('/api/chat/openai', { method: 'POST', body: JSON.stringify({ messages, model, session_id }) }),
  chatClaude: (messages, model, session_id) => request('/api/chat/anthropic', { method: 'POST', body: JSON.stringify({ messages, model, session_id }) }),
  // 2K/4K рендерятся дольше обычных 120 с; nginx на сервере держит 600 с
  generateImage: (prompt, size, session_id, imageModel, resolution, editFile) => request('/api/chat/image', { method: 'POST', body: JSON.stringify({ prompt, size, resolution, session_id, imageModel, editFile }), _timeout: 300000 }),
  generateVideo: (prompt, model, size, seconds, session_id, refFile) => request('/api/chat/video', { method: 'POST', body: JSON.stringify({ prompt, model, size, seconds, session_id, refFile }) }),
  videoStatus: (id) => request(`/api/chat/video/${id}`),
  myVideos: () => request('/api/chat/my-videos'),
  uploadChatFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API}/api/chat/upload-file`, { method: 'POST', headers: authHeader(), body: formData });
    const data = await res.json();
    if (res.status === 401) { handleUnauthorized(); throw new Error('Сессия истекла. Войдите снова.'); }
    if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
    return data;
  },
  saveTheme: (theme_settings) => request('/api/users/theme', { method: 'POST', body: JSON.stringify({ theme_settings }) }),
  chatHistory: (limit) => request(`/api/chat/history?limit=${limit || 50}`),
  mySessions: () => request('/api/chat/my-sessions'),
  loadSession: (sessionId) => request(`/api/chat/session/${sessionId}`),
  statsUsage: () => request('/api/stats/usage'),
  statsAnalytics: () => request('/api/stats/analytics'),
  statsMy: () => request('/api/stats/my'),
  adminSessions: (userId) => request(`/api/chat/admin/sessions${userId ? '?user_id=' + userId : ''}`),
  adminSession: (sessionId) => request(`/api/chat/admin/session/${sessionId}`),
  adminSearchChats: (q) => request(`/api/chat/admin/search?q=${encodeURIComponent(q)}`),
  adminImages: (userId) => request(`/api/chat/admin/images?user_id=${userId}`),
  adminVideos: (userId) => request(`/api/chat/admin/videos?user_id=${userId}`),

  // Files & Skills
  getMyFiles: () => request('/api/files/my'),
  getGlobalFiles: () => request('/api/files/global'),
  getSkills: () => request('/api/files/skills'),
  saveSkills: (skills_text) => request('/api/files/skills', { method: 'POST', body: JSON.stringify({ skills_text }) }),
  deleteFile: (id) => request(`/api/files/${id}`, { method: 'DELETE' }),
  getUserFiles: (userId) => request(`/api/files/user/${userId}`),
  // Permissions
  getPermissionsList: () => request('/api/permissions/list'),
  getUserPermissions: (userId) => request(`/api/permissions/user/${userId}`),
  saveUserPermissions: (userId, permissions) => request(`/api/permissions/user/${userId}`, { method: 'POST', body: JSON.stringify({ permissions }) }),

  // UI Visibility
  getUIElements: () => request('/api/permissions/ui-elements'),
  getUIVisibility: () => request('/api/permissions/ui-visibility'),
  saveUIVisibility: (visibility) => request('/api/permissions/ui-visibility', { method: 'POST', body: JSON.stringify({ visibility }) }),

  // Limits
  getLimits: () => request('/api/limits'),
  setLimit: (userId, limit_usd, period_days) => request(`/api/limits/${userId}`, { method: 'POST', body: JSON.stringify({ limit_usd, period_days }) }),
  resetLimit: (userId) => request(`/api/limits/${userId}/reset`, { method: 'POST' }),
  checkLimit: () => request('/api/limits/check'),

  // Alerts
  getAlerts: () => request('/api/alerts'),
  readAlert: (id) => request(`/api/alerts/${id}/read`, { method: 'POST' }),
  readAllAlerts: () => request('/api/alerts/read-all', { method: 'POST' }),

  // Backups (БЭКАП БД)
  listBackups: () => request('/api/backup/list'),
  createBackup: () => request('/api/backup/create', { method: 'POST', _timeout: 120000 }),
  restoreBackup: (name) => request(`/api/backup/restore-backup/${name}`, { method: 'POST' }),
  deleteBackup: (name) => request(`/api/backup/backup/${name}`, { method: 'DELETE' }),

  // Snapshots (РЕЗЕРВ СИСТЕМЫ)
  createSnapshot: () => request('/api/backup/snapshot', { method: 'POST', _timeout: 300000 }),
  listSnapshots: () => request('/api/backup/snapshots'),
  restoreSnapshot: (name) => request(`/api/backup/restore/${name}`, { method: 'POST' }),
  deleteSnapshot: (name) => request(`/api/backup/snapshot/${name}`, { method: 'DELETE' }),

  // Estimates (Расчёт сметы)
  getEstimates: () => request('/api/estimates'),
  getEstimate: (id) => request(`/api/estimates/${id}`),
  createEstimate: (data) => request('/api/estimates', { method: 'POST', body: JSON.stringify(data) }),
  updateEstimate: (id, data) => request(`/api/estimates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteEstimate: (id) => request(`/api/estimates/${id}`, { method: 'DELETE' }),
  saveEstimateItems: (id, items) => request(`/api/estimates/${id}/items`, { method: 'POST', body: JSON.stringify({ items }) }),
  uploadEstimateFiles: async (id, files) => {
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    const res = await fetch(`${API}/api/estimates/${id}/files`, { method: 'POST', headers: authHeader(), body: formData });
    const data = await res.json();
    if (res.status === 401) { handleUnauthorized(); throw new Error('Сессия истекла. Войдите снова.'); }
    if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
    return data;
  },
  deleteEstimateFile: (fileId) => request(`/api/estimates/files/${fileId}`, { method: 'DELETE' }),
  addEstimateComment: (id, text) => request(`/api/estimates/${id}/comments`, { method: 'POST', body: JSON.stringify({ text }) }),
  deleteEstimateComment: (commentId) => request(`/api/estimates/comments/${commentId}`, { method: 'DELETE' }),
  exportEstimatePDF: (id) => `${API}/api/estimate-export/${id}/pdf?token=${localStorage.getItem('token')}`,
  exportEstimateExcel: (id) => `${API}/api/estimate-export/${id}/excel?token=${localStorage.getItem('token')}`,
  getEstimateCatalog: () => request('/api/estimates/catalog/all'),
  seedEstimateCatalog: () => request('/api/estimates/catalog/seed', { method: 'POST' }),
  attachImageToEstimate: (id, image_url, original_name) => request(`/api/estimates/${id}/attach-image`, { method: 'POST', body: JSON.stringify({ image_url, original_name }) }),
  recognizeEstimate: (id, models, standParams) => request(`/api/estimates/${id}/recognize`, { method: 'POST', body: JSON.stringify({ models, standParams }), _timeout: 300000 }),
  quickCreateEstimate: (project_title) => request('/api/estimates/quick-create', { method: 'POST', body: JSON.stringify({ project_title }) }),
  getEstimatesList: () => request('/api/estimates'),
  getEstimateTaxSettings: () => request('/api/estimates/tax-settings'),

  // Терминал сметной системы (только админ)
  terminalState: () => request('/api/estimate-terminal/state'),
  terminalSend: (text, model) => request('/api/estimate-terminal/message', { method: 'POST', body: JSON.stringify({ text, model }), _timeout: 240000 }),
  terminalAddRule: (text) => request('/api/estimate-terminal/rules', { method: 'POST', body: JSON.stringify({ text }) }),
  terminalUpdateRule: (id, data) => request(`/api/estimate-terminal/rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  terminalSaveSettings: (data) => request('/api/estimate-terminal/settings', { method: 'PUT', body: JSON.stringify(data) }),

  // Training
  getTrainingProjects: () => request('/api/training/projects'),
  createTrainingProject: (name, description) => request('/api/training/projects', { method: 'POST', body: JSON.stringify({ name, description }) }),
  deleteTrainingProject: (id) => request(`/api/training/projects/${id}`, { method: 'DELETE' }),
  getTrainingFiles: (projectId) => request(`/api/training/projects/${projectId}/files`),
  uploadTrainingFiles: async (projectId, files) => {
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    const token = localStorage.getItem('token');
    const resp = await fetch(`${API}/api/training/projects/${projectId}/files`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
    if (resp.status === 401) { handleUnauthorized(); throw new Error('Сессия истекла. Войдите снова.'); }
    if (!resp.ok) throw new Error('Upload failed');
    return resp.json();
  },
  deleteTrainingFile: (fileId) => request(`/api/training/files/${fileId}`, { method: 'DELETE' }),
  startTraining: (project_ids) => request('/api/training/train', { method: 'POST', body: JSON.stringify({ project_ids }), _timeout: 600000 }),
  getKnowledgeBase: () => request('/api/training/knowledge-base'),
  getKnowledgeStats: () => request('/api/training/knowledge-stats'),

  // Balance
  getBalance: () => request('/api/balance'),

  // Org description
  getOrgDescription: () => request('/api/files/org-description'),
  saveOrgDescription: (org_description) => request('/api/files/org-description', { method: 'POST', body: JSON.stringify({ org_description }) }),

  uploadFiles: async (files, isGlobal = false) => {
    const formData = new FormData();
    const fileNames = [];
    for (const f of files) { formData.append('files', f); fileNames.push(f.name); }
    formData.append('original_names', JSON.stringify(fileNames));
    if (isGlobal) formData.append('is_global', '1');
    const res = await fetch(`${API}/api/files/upload`, { method: 'POST', headers: authHeader(), body: formData });
    const data = await res.json();
    if (res.status === 401) { handleUnauthorized(); throw new Error('Сессия истекла. Войдите снова.'); }
    if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
    return data;
  },
};
